import type { ConversationMetadata } from 'types/ai.types';
import { useSetAiMetadata } from '.';
import type { Gratification, HelperContent } from 'utils/gratifications/types';
import { ContentType } from 'utils/gratifications/types';
import i18n from 'i18n';

const helperContentToText = (content: HelperContent[], depth = 0): string => {
  if (!content) return '';
  return content
    .map((c) => {
      switch (c.type) {
        case ContentType.title:
          return `# ${c.text ? i18n.t(c.text) : ''}`;
        case ContentType.subTitle:
          return `## ${c.text ? i18n.t(c.text) : ''}`;
        case ContentType.text:
        case ContentType.subText:
          return c.text ? i18n.t(c.text) : '';
        case ContentType.link:
          return c.text ? `[${i18n.t(c.text)}](${c.to || '#'})` : '';
        case ContentType.container:
          // Recursively render innerContent
          return c.innerContent
            ? helperContentToText(c.innerContent, depth + 1)
            : '';
        case ContentType.divider:
          return '\n---\n';
        case ContentType.image:
          return c.imgSrc
            ? `![${c.text ? i18n.t(c.text) : ''}](${c.imgSrc})`
            : '';
        case ContentType.date:
          return c.text ? `_${i18n.t(c.text)}_` : '';
        // Render textual content for table link/buttons as plain text to include in AI context
        case ContentType.tableLink:
        case ContentType.button:
        case ContentType.outlineButton:
        case ContentType.floatingButton:
        case ContentType.textInput:
          return c.text ? i18n.t(c.text) : '';
        case ContentType.table: {
          const rows = c?.innerContent?.filter(({ type }) =>
            [ContentType.tableRowTitle, ContentType.tableRow].includes(type),
          );
          if (!rows?.length) return '';

          const header = rows
            .filter(({ type }) => type === ContentType.tableRowTitle)
            .map(({ text }) => (text ? i18n.t(text) : ''))
            .join(' | ');
          const divider = header
            .split('|')
            .map(() => '---')
            .join(' | ');
          const body = rows
            .filter(({ type }) => type === ContentType.tableRow)
            .map(({ text, value }) =>
              [text ? i18n.t(text) : '', value ? i18n.t(value) : ''].join(
                ' | ',
              ),
            )
            .join('\n');

          return [header, divider, body].filter(Boolean).join('\n');
        }
      }
    })
    .filter(Boolean)
    .join('\n\n');
};

export const mapGratificationMetadata = (
  gratification: Partial<Gratification>,
): ConversationMetadata => ({
  documentId: gratification?._id,
  documentType: 'gratifications',
  content: helperContentToText(gratification.helperContent || []),
});

export const useGratificationAiMetadata = (
  gratification?: Partial<Gratification>,
): void =>
  useSetAiMetadata(gratification, 'gratifications', mapGratificationMetadata);
